import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TransformWrapper } from 'react-zoom-pan-pinch';

import { Paper } from '@material-ui/core';
import { TEACHER_ASSIGNMENT_V2_INSIGHTS_URL } from 'config/urls';
import { get } from 'lodash';

import {
  addAssignmentAudio,
  postAssignmentComment,
  updateAssignmentStatus
} from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/sdk';
import {
  getObjectFromList,
  loadImageFromSrc,
  uploadImageBlob
} from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/utils';
import {
  useEditingContext,
  useFastContext
} from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/utilsEditingContext';
import {
  uploadAudioFile,
  useAudioRecorder
} from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/utilsRecorder';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';

import AudioPlayer from './components/AudioPlayer';
import Comments from './components/Comments';
import EditingCanvas from './components/EditingCanvas';
import { getInitialImage } from './components/EditingCanvas/utils';
import Header from './components/Header';
import RightMenu from './components/RightMenu';
import StudentWorkVersions from './components/StudentWorkVersions';
import ControllsOverImage from 'components/ControllsOverImage';
import TrackerInstanceSectionContext from 'components/TrackerInstanceSectionContext';
import UserContext from 'components/UserContext';

import styles from './styles.module.scss';

const FeedbackEditingTool = ({
  activeWork,
  activeTask,
  setActiveTask,
  setActiveWork,
  studentName,
  refetchAssignment,
  gotoPrevTask,
  gotoNextTask,
  gotoNextUngradedTask
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showStickers, setShowStickers] = useState(false);

  const { user } = useContext(UserContext);
  const { class_id, standards } = useContext(TrackerInstanceSectionContext);

  const imageSrc = useMemo(() => activeWork.s3_work_url, [activeWork]);

  const fastContext = useFastContext();
  const editingContext = useEditingContext(imageSrc);

  const gradeLightFeedbacks = get(activeWork, 'grade_light_feedbacks', []);
  const feedbackText = useMemo(
    // If feedbacks are more than one, then we have revision and we should not display the ai feedback anymore
    () =>
      gradeLightFeedbacks?.length === 1
        ? gradeLightFeedbacks[0]['feedback'][0]
        : '',
    [gradeLightFeedbacks]
  );

  const feedbackExplanation = useMemo(
    () =>
      gradeLightFeedbacks?.length === 1
        ? gradeLightFeedbacks[0]['explanation']
        : '',
    [gradeLightFeedbacks]
  );

  useEffect(() => {
    if (imageSrc && fastContext.isReady) {
      const url = `${imageSrc}?dont-cache=true`;
      loadImageFromSrc(url).then((image) => {
        fastContext.drawInitialImageObject(image);
        if (feedbackText !== '') {
          fastContext.drawInitialFeedback(feedbackText);
        }
      });
    }
  }, [imageSrc, fastContext, feedbackText]);

  const recorder = useAudioRecorder({
    onAudioRecorded: async (audioBlob) => {
      const feedbackFileId = await uploadAudioFile(audioBlob);

      await addAssignmentAudio(activeTask?.id, { file: feedbackFileId });

      trackMixpanelEvent(user.id, 'Teacher adds audio feedback on work', {
        feature: 'insights',
        view: 'student work',
        button: 'audio feedback',
        standards,
        class_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      });

      const assignment = await refetchAssignment();

      const refreshedActiveTask = assignment.tasks.find(
        (task) => task.id === activeTask?.id
      );

      if (refreshedActiveTask) {
        setActiveTask((prev) => ({
          ...prev,
          audio_files: refreshedActiveTask.audio_files
        }));
      }
    }
  });

  const handleTaskStatusUpdateOnClick = async (newStatus, autoAdvance) => {
    setIsLoading(true);

    // Handle cropping and discard current selection
    if (editingContext.cropping.active) {
      editingContext.cropping.cropImage();
    }

    fastContext.discardSelection();

    const blobPromise = fastContext.imageBlobFromCanvas();

    const initialImage = getInitialImage(fastContext.canvas);

    const imageWasCropped = initialImage?.scaleX !== 1;
    const imageWasRotated = (initialImage?.angle || 0) % 360 !== 0;
    const canvasHasNewObjects =
      fastContext.hasNewObjects() || imageWasCropped || imageWasRotated;

    const feedbackTexts = canvasHasNewObjects
      ? fastContext.canvas
          .getObjects()
          .map((obj) => obj.text)
          .filter(Boolean)
      : [];

    const cachedActiveTask = activeTask;

    // Update assignment status (advance handled manually below)
    await updateAssignmentStatus({
      taskId: cachedActiveTask.id,
      data: { status: newStatus, advanced_switch_enabled: false }
    });

    trackMixpanelEvent(user.id, `Teacher marks work as ${newStatus}`, {
      feature: 'insights',
      view: 'student work',
      button: newStatus,
      standards,
      class_id,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
    });

    // Handle canvas export and save
    const blob = await blobPromise;

    if (canvasHasNewObjects) {
      await uploadImageBlob(blob, activeWork, feedbackTexts);

      trackMixpanelEvent(user.id, `Teacher adds feedback on work`, {
        feature: 'insights',
        view: 'student work',
        button: 'feedback',
        standards,
        class_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      });

      if (autoAdvance) {
        gotoNextUngradedTask();
      } else {
        const assignment = await refetchAssignment();
        const fetchedTask = getObjectFromList(assignment.tasks, activeTask.id);

        setActiveTask(fetchedTask);
      }
    } else if (autoAdvance) {
      gotoNextUngradedTask();
    }

    setIsLoading(false);
  };

  const handleAddComment = async ({
    values,
    resetForm,
    activeTask,
    setActiveTask,
    refetchAssignment
  }) => {
    await postAssignmentComment({
      taskId: activeTask.id,
      data: values
    });
    resetForm();

    trackMixpanelEvent(user.id, 'Teacher adds comment on work', {
      feature: 'insights',
      view: 'student work',
      button: 'comment',
      standards,
      class_id,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
    });

    const assignment = await refetchAssignment();

    const refreshedActiveTask = assignment.tasks.find(
      (task) => task.id === activeTask?.id
    );

    if (refreshedActiveTask) {
      setActiveTask((prev) => ({
        ...prev,
        comments: refreshedActiveTask.comments
      }));
    }
  };

  return (
    <div className={styles.container}>
      <Paper variant="outlined" className={styles.feedbackPaper}>
        <Header
          studentName={studentName}
          activeTask={activeTask}
          gotoPrevTask={gotoPrevTask}
          gotoNextTask={gotoNextTask}
        />

        <div className={styles.feedbackImage} id="image-container">
          <TransformWrapper
            options={{
              maxScale: 11,
              disabled: false
            }}
            zoomIn={{ step: 15 }}
            zoomOut={{ step: 15 }}
            wheel={{ disabled: true }}
            pan={{
              paddingSize: 0,
              disabled:
                editingContext.objectDeletion.hasSelectedObject ||
                editingContext.draw.isActive ||
                editingContext.cropping.active
            }}
            doubleClick={{ disabled: true }}
          >
            {({ scale, zoomOut, zoomIn, resetTransform }) => {
              return (
                <>
                  <EditingCanvas
                    editingContext={editingContext}
                    resetTransform={resetTransform}
                    fastContext={fastContext}
                  />
                  <ControllsOverImage
                    recorder={recorder}
                    showStickers={showStickers}
                    closeStickers={() => setShowStickers(false)}
                    editingContext={editingContext}
                    fastContext={fastContext}
                    zoom={{ zoomIn, zoomOut, scale }}
                  />
                </>
              );
            }}
          </TransformWrapper>
        </div>
        <StudentWorkVersions
          activeTask={activeTask}
          activeWork={activeWork}
          setActiveWork={setActiveWork}
        />
        <AudioPlayer activeTask={activeTask} />
        <Comments
          key={get(activeTask, 'id')}
          comments={get(activeTask, 'comments', [])}
          addComment={async (values, { resetForm }) => {
            await handleAddComment({
              values,
              resetForm,
              activeTask,
              setActiveTask,
              refetchAssignment
            });
          }}
        />
      </Paper>

      {activeTask && (
        <RightMenu
          onClickAssignmentStatus={handleTaskStatusUpdateOnClick}
          feedbackExplanation={feedbackExplanation}
          onTaskUpdate={setActiveTask}
          activeTask={activeTask}
          activeWork={activeWork}
          rotateImage={editingContext.rotation.rotateCanvas}
          cropButton={{
            active: editingContext.cropping.active,
            toggle: editingContext.cropping.activate,
            turnOff: editingContext.cropping.turnOff,
            cropImage: editingContext.cropping.cropImage
          }}
          filterButton={{
            active: editingContext.filter.isActive,
            toggle: editingContext.filter.toggle,
            turnOff: editingContext.filter.turnOff,
            apply: editingContext.filter.apply,
            remove: editingContext.filter.remove
          }}
          drawButton={{
            active: editingContext.draw.isActive,
            toggle: editingContext.draw.toggle,
            turnOff: editingContext.draw.turnOff
          }}
          textButton={{
            apply: editingContext.textBox.addTextBox
          }}
          stickersButton={{
            active: showStickers,
            toggle: () => setShowStickers(!showStickers),
            turnOff: () => setShowStickers(false)
          }}
          audioButton={{
            active: recorder.isVisible,
            toggle: recorder.toggleVisible,
            isRecording: recorder.isActive,
            turnOff: () => recorder.setVisible(false)
          }}
          undo={{
            isEmpty: editingContext.undo.isEmpty,
            pop: editingContext.undo.pop,
            clear: editingContext.undo.clear
          }}
          zoom={{ zoomToNormal: editingContext.zoom.zoomToNormal }}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default FeedbackEditingTool;
